const BERLIN_TZ = 'Europe/Berlin';
export function normalizeClassName(name) {
    return name.trim().toLowerCase();
}
/**
 * Splits a free-text search query into lowercased, whitespace-separated tokens.
 * Empty tokens (from leading/trailing/duplicate spaces) are dropped, so an
 * all-whitespace query yields `[]`.
 *
 * Shared by the CheckOut client filter and the backend `/dashboard/students`
 * search so both sides tokenize identically (CLAUDE.md §dashboard search).
 */
export function tokenizeSearch(query) {
    return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}
/**
 * AND-over-tokens, OR-over-fields contains match (case-insensitive). A student
 * matches when EVERY token is a substring of at least ONE of firstName,
 * lastName or className. An empty/whitespace-only query matches everything.
 *
 * Used by the CheckOut client filter; the backend builds the equivalent Prisma
 * where-clause from the same `tokenizeSearch` output.
 */
export function studentMatchesSearch(student, query) {
    const tokens = tokenizeSearch(query);
    if (tokens.length === 0)
        return true;
    const haystack = [student.firstName, student.lastName, student.className].map((f) => f.toLowerCase());
    return tokens.every((t) => haystack.some((f) => f.includes(t)));
}
/**
 * Formats a Date as "YYYY-MM-DD" in Europe/Berlin local time.
 * Uses Intl (not date-fns-tz) per CLAUDE.md §10 — /shared has no
 * external date-fns-tz dependency.
 */
export function formatDateStr(date) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: BERLIN_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    return fmt.format(date);
}
/**
 * Returns the date of the last Sunday of the given month (0-indexed: Jan=0).
 * Used by parametrized DST tests (CLAUDE.md §28.1) — DST starts on the last
 * Sunday of March and ends on the last Sunday of October in Europe/Berlin.
 *
 * The returned Date is a UTC midnight on the target day; callers that care
 * about the wall-clock day in Berlin should pass it through `formatDateStr`.
 */
export function findLastSundayOfMonth(year, monthIndex) {
    const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0));
    const dayOfWeek = lastDay.getUTCDay();
    return new Date(Date.UTC(year, monthIndex, lastDay.getUTCDate() - dayOfWeek));
}
/**
 * Formats a ProjectDay date as DE short format "dd.MM.yy" for compact column
 * headers in Berichte-Tabs. Accepts either a "YYYY-MM-DD" string (typical
 * Backend payload) or a Date.
 *
 * String inputs are split via regex to avoid the UTC-midnight drift that
 * `new Date("YYYY-MM-DD")` introduces — that constructor parses as UTC, which
 * renders as the previous day in Berlin between 00:00 and ~02:00 local time.
 *
 * Throws on malformed string input so callers fail loud rather than render
 * nonsense.
 */
export function formatProjectDayDate(date) {
    if (typeof date === 'string') {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
        if (!m)
            throw new Error(`formatProjectDayDate: invalid date string "${date}"`);
        return `${m[3]}.${m[2]}.${m[1].slice(2)}`;
    }
    return new Intl.DateTimeFormat('de-DE', {
        timeZone: BERLIN_TZ,
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}
//# sourceMappingURL=utils.js.map